import React, { useEffect, useRef } from 'react';
import {
  Box,
  Step,
  StepConnector,
  stepConnectorClasses,
  StepLabel,
  Stepper,
  styled,
} from '@mui/material';

const ProgressConnector = styled(StepConnector)(({ theme }) => ({
  [`&.${stepConnectorClasses.alternativeLabel}`]: {
    top: 17,
  },
  [`& .${stepConnectorClasses.line}`]: {
    borderColor: 'rgba(28, 27, 31, 0.16)',
    borderTopWidth: 2,
    borderRadius: 999,
  },
  [`&.${stepConnectorClasses.active} .${stepConnectorClasses.line}`]: {
    borderColor: theme.palette.primary.main,
  },
  [`&.${stepConnectorClasses.completed} .${stepConnectorClasses.line}`]: {
    borderColor: theme.palette.primary.main,
  },
}));

const StepIconRoot = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'ownerState',
})(({ theme, ownerState }) => ({
  width: 34,
  height: 34,
  borderRadius: '50%',
  display: 'grid',
  placeItems: 'center',
  fontSize: 16,
  fontWeight: 700,
  transition: 'all 180ms ease',
  border: `2px solid ${
    ownerState.active || ownerState.completed
      ? theme.palette.primary.main
      : 'rgba(79, 133, 6, 0.42)'
  }`,
  color:
    ownerState.active || ownerState.completed
      ? theme.palette.primary.contrastText
      : theme.palette.primary.main,
  backgroundColor:
    ownerState.active || ownerState.completed
      ? theme.palette.primary.main
      : theme.palette.background.paper,
  boxShadow: ownerState.active ? '0 0 0 6px rgba(79, 133, 6, 0.10)' : 'none',
}));

const SignupStepIcon = ({ active, completed, icon }) => {
  return (
    <StepIconRoot ownerState={{ active, completed }}>
      <Box component="span">{icon}</Box>
    </StepIconRoot>
  );
};

export const SignupProgress = ({ steps, activeStep }) => {
  const scrollerRef = useRef(null);
  const stepRefs = useRef([]);

  useEffect(() => {
    const scroller = scrollerRef.current;
    const activeStepNode = stepRefs.current[activeStep];

    if (!scroller || !activeStepNode) {
      return;
    }

    const targetLeft =
      activeStepNode.offsetLeft - scroller.clientWidth / 2 + activeStepNode.clientWidth / 2;

    scroller.scrollTo({
      left: Math.max(0, targetLeft),
      behavior: 'smooth',
    });
  }, [activeStep, steps.length]);

  return (
    <Box
      ref={scrollerRef}
      sx={{
        width: '100%',
        overflowX: { xs: 'auto', md: 'visible' },
        overflowY: 'hidden',
        pb: { xs: 1, md: 0 },
        '&::-webkit-scrollbar': {
          display: 'none',
        },
        scrollbarWidth: 'none',
      }}
    >
      <Stepper
        alternativeLabel
        activeStep={activeStep}
        connector={<ProgressConnector />}
        sx={{
          width: { xs: 'max-content', md: '100%' },
          minWidth: { xs: `${Math.max(steps.length, 5) * 116}px`, md: '100%' },
          px: { xs: 0.5, md: 2 },
          '& .MuiStepLabel-label': {
            mt: 1,
            fontSize: { xs: 11, sm: 12, md: 14 },
            fontWeight: 400,
            color: 'text.secondary',
            whiteSpace: 'nowrap',
          },
          '& .MuiStepLabel-label.Mui-active': {
            color: 'text.primary',
            fontWeight: 700,
          },
          '& .MuiStepLabel-label.Mui-completed': {
            color: 'text.primary',
            fontWeight: 400,
          },
          '& .MuiStep-root': {
            px: { xs: 0.5, md: 1 },
          },
        }}
      >
        {steps.map((label, index) => (
          <Step
            key={label}
            ref={(node) => {
              stepRefs.current[index] = node;
            }}
          >
            <StepLabel StepIconComponent={SignupStepIcon}>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>
    </Box>
  );
};

export default SignupProgress;
